import { gql } from "@apollo/client";
import {
  Badge,
  Button,
  ButtonGroup,
  FormControl,
  FormLabel,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverCloseButton,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  Stack,
  Switch,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import React, { Fragment, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { FormattedDate } from "react-intl";
import { Link } from "react-router-dom";

import { EditIcon, PlusIcon } from "../../../components/icons";
import { PageTitle } from "../../../components/Page";
import { PageSpinner } from "../../../components/PageSpinner";
import { Pagination } from "../../../components/Pagination";
import { Panel } from "../../../components/Panel";
import { StyledLink } from "../../../components/StyledLink";
import { Table } from "../../../components/Table";
import { buildAdminEditEWCCodePath, buildAdminNewEWCCodePath } from "../../../config/routes";
import {
  GetEwcCodesQuery,
  SortOrder,
  useEwcCodeListArchiveEwcCodeMutation,
  useEwcCodeListUnarchiveEwcCodeMutation,
  useGetEwcCodesQuery,
} from "../../../generated/graphql";
import { getGraphQLErrorMessage } from "../../../utils/getGraphQLErrorMessage";

export const EWC_CODE_LIST_GET_EWC_CODES = gql`
  query GetEWCCodes(
    $offset: Int
    $limit: Int
    $includeArchived: Boolean
    $orderBy: [QueryEwcCodesOrderByInput!]
  ) {
    ewcCodes(skip: $offset, take: $limit, includeArchived: $includeArchived, orderBy: $orderBy) {
      id
      code
      product
      note
      isArchived
      archivedAt
    }

    ewcCodeCount(includeArchived: $includeArchived)
  }
`;

export const EWC_CODE_LIST_ARCHIVE_EWC_CODE = gql`
  mutation EWCCodeListArchiveEWCCode($ewcCodeId: String!) {
    archiveEWCCode(input: { ewcCodeId: $ewcCodeId }) {
      id
      code
      product
      note
      archivedAt
      isArchived
      updatedAt
    }
  }
`;

export const EWC_CODE_LIST_UNARCHIVE_EWC_CODE = gql`
  mutation EWCCodeListUnarchiveEWCCode($ewcCodeId: String!) {
    unarchiveEWCCode(input: { ewcCodeId: $ewcCodeId }) {
      id
      code
      product
      note
      archivedAt
      isArchived
      updatedAt
    }
  }
`;

type EWCCodeRow = GetEwcCodesQuery["ewcCodes"][0];

export const EWCCodeListPage = () => {
  const [showArchived, setShowArchived] = React.useState(false);
  const [paginationData, setPaginationData] = React.useState({
    currentPage: 1,
    pageSize: 25,
    totalPages: 0,
  });

  const { loading, error, data } = useGetEwcCodesQuery({
    variables: {
      offset: (paginationData.currentPage - 1) * paginationData.pageSize,
      limit: paginationData.pageSize,
      includeArchived: showArchived,
      // With includeArchived and no orderBy the API lists active codes first, then archived, each
      // by code. Without includeArchived it doesn't sort by code, so ask for that explicitly.
      orderBy: showArchived ? undefined : [{ code: SortOrder.Asc }],
    },
  });

  // Archiving the last code on a page (while archived codes are hidden) leaves the page empty
  const isPageEmpty = data?.ewcCodes.length === 0;
  React.useEffect(() => {
    if (isPageEmpty && paginationData.currentPage > 1) {
      setPaginationData((current) => ({ ...current, currentPage: current.currentPage - 1 }));
    }
  }, [isPageEmpty, paginationData.currentPage]);

  if (loading) {
    return <PageSpinner />;
  }

  if (error) {
    console.error(error);
    return (
      <Stack spacing={4}>
        <Text>Something went wrong loading the EWC Codes</Text>
      </Stack>
    );
  }

  return (
    <Fragment>
      <Helmet>
        <title>WMS | EWC Codes</title>
      </Helmet>
      <Stack spacing={6} px={8} py={5}>
        <Stack width="100%" isInline justifyContent="space-between" alignItems="center">
          <PageTitle>EWC Codes</PageTitle>
          <Stack isInline spacing={6} alignItems="center">
            <FormControl display="flex" alignItems="center" width="auto">
              <FormLabel htmlFor="show-archived" mb={0}>
                Show archived
              </FormLabel>
              <Switch
                id="show-archived"
                isChecked={showArchived}
                onChange={(e) => {
                  setShowArchived(e.target.checked);
                  setPaginationData({ ...paginationData, currentPage: 1 });
                }}
              />
            </FormControl>
            <ButtonGroup>
              <Button
                as={Link}
                to={buildAdminNewEWCCodePath()}
                colorScheme="blue"
                leftIcon={<PlusIcon />}
              >
                Create new EWC Code
              </Button>
            </ButtonGroup>
          </Stack>
        </Stack>

        <Panel height="65vh" overflowY="auto">
          <Table
            data-testid="ewc-codes-list"
            data={data?.ewcCodes ?? []}
            columns={ewcCodeTableColumns}
            getRowProps={(ewcCode: EWCCodeRow) => (ewcCode.isArchived ? { color: "gray.500" } : {})}
          />
        </Panel>
        <Pagination
          currentPage={paginationData.currentPage}
          pageSize={paginationData.pageSize}
          totalCount={data?.ewcCodeCount ?? 1}
          onGoToPage={(pageNumber) => {
            setPaginationData({
              ...paginationData,
              currentPage: pageNumber,
            });
          }}
        />
      </Stack>
    </Fragment>
  );
};

const EWCCodeArchiveToggleButton = ({ ewcCode }: { ewcCode: EWCCodeRow }) => {
  const toast = useToast();
  const { onOpen, onClose, isOpen } = useDisclosure();
  const initialFocusRef = useRef<HTMLButtonElement>(null);

  const [archiveEWCCode, { loading: archiving }] = useEwcCodeListArchiveEwcCodeMutation({
    refetchQueries: ["GetEWCCodes"],
  });
  const [unarchiveEWCCode, { loading: unarchiving }] = useEwcCodeListUnarchiveEwcCodeMutation({
    refetchQueries: ["GetEWCCodes"],
  });

  const runMutation = async (mutate: typeof archiveEWCCode | typeof unarchiveEWCCode) => {
    try {
      await mutate({ variables: { ewcCodeId: ewcCode.id } });
    } catch (e) {
      // The API's messages are user-facing, e.g. why an unarchive was rejected
      toast({
        title: getGraphQLErrorMessage(e),
        status: "error",
        isClosable: true,
        duration: 9000,
      });
    }
  };

  if (ewcCode.isArchived) {
    return (
      <Button
        variant="outline"
        isLoading={unarchiving}
        isDisabled={unarchiving}
        onClick={() => runMutation(unarchiveEWCCode)}
      >
        Unarchive
      </Button>
    );
  }

  return (
    <Popover isOpen={isOpen} initialFocusRef={initialFocusRef} onOpen={onOpen} onClose={onClose}>
      <PopoverTrigger>
        <Button variant="outline" isLoading={archiving} isDisabled={archiving}>
          Archive
        </Button>
      </PopoverTrigger>
      <PopoverContent color="initial">
        <PopoverHeader pt={4} fontWeight="bold" border="0">
          Archive {ewcCode.code} – {ewcCode.product}? It will no longer be selectable on collection
          notes.
        </PopoverHeader>
        <PopoverArrow />
        <PopoverCloseButton />
        <PopoverFooter>
          <ButtonGroup size="sm">
            <Button
              colorScheme="red"
              ref={initialFocusRef}
              onClick={() => {
                onClose();
                runMutation(archiveEWCCode);
              }}
            >
              Archive
            </Button>
            <Button colorScheme="gray" onClick={onClose}>
              Cancel
            </Button>
          </ButtonGroup>
        </PopoverFooter>
      </PopoverContent>
    </Popover>
  );
};

const CodeAccessor = (ewcCode: EWCCodeRow) => {
  return {
    id: ewcCode.id,
    code: ewcCode.code,
  };
};

const RowAccessor = (ewcCode: EWCCodeRow) => ewcCode;

const ewcCodeTableColumns = [
  {
    Header: "Code",
    accessor: CodeAccessor,
    Cell: (props: { value: ReturnType<typeof CodeAccessor> }) => {
      const { id, code } = props.value;

      return <StyledLink to={buildAdminEditEWCCodePath(id)}>{code}</StyledLink>;
    },
  },
  { Header: "Product", accessor: "product" },
  { Header: "Note", accessor: "note" },
  {
    id: "status",
    Header: "Status",
    accessor: RowAccessor,
    Cell: (props: { value: EWCCodeRow }) => {
      const { isArchived, archivedAt } = props.value;

      return isArchived ? (
        <Stack spacing={1} alignItems="flex-start">
          <Badge colorScheme="gray">Archived</Badge>
          {archivedAt && (
            <Text fontSize="xs">
              <FormattedDate value={archivedAt} day="numeric" month="short" year="numeric" />
            </Text>
          )}
        </Stack>
      ) : (
        <Badge colorScheme="green">Active</Badge>
      );
    },
  },
  {
    id: "actions",
    Header: "",
    accessor: RowAccessor,
    Cell: (props: { value: EWCCodeRow }) => {
      return (
        <ButtonGroup>
          <Link to={`${props.value.id}/edit`}>
            <IconButton colorScheme="green" aria-label="Edit EWC Code" icon={<EditIcon />} />
          </Link>
          <EWCCodeArchiveToggleButton ewcCode={props.value} />
        </ButtonGroup>
      );
    },
  },
];
